import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get, set } from 'firebase/database';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Image as Eye, Info, Check, AlertCircle, Undo2, Trash2, ArrowLeft, RotateCcw, Wrench, Search } from 'lucide-react';
import './MaintenanceDraft.css';
import { BaseComponentImpl } from '../../../types/implementations';
import { ComponentLifetime, MaintenanceTemplate, TemplatesByType, getLifetimeForDirection, getIntervalForDirection } from '../TemplatesEditor/types';
import { MaintenanceIntervalType } from '../../../types/Enums';
import { formatDateToString, translateIntervalType, intervalToDays } from '../../../Helpers/maintenanceCostUtils';
import { Building } from '../../../types/protocols';
import { translate } from '../../../types/EnumTranslations';
import { RELEVANT_BASE_TYPES, BASE_TYPE_GROUPS } from '../../../Helpers/baseTypeUtils';
import { ConditionAssessmentSettings, ConditionAssessmentCategory, CONDITION_ASSESSMENT_CATEGORIES, DEFAULT_CONDITION_ASSESSMENT_SETTINGS } from '../../BuildingChecks/types';
import PhotoGallery from '../../../Components/PhotoGallery';
import { computeShapeArea } from '../../../types/GeometryShapes';
import MaintenancePreview from '../MaintenancePreview/MaintenancePreview';
import { getChartColor } from 'Helpers/chartColors';
import { useUserNames } from '../../../Helpers/useUserNames';
import { Helmet } from 'react-helmet-async';


interface ComponentWithReplacement {
    component: BaseComponentImpl;
    lifetime: ComponentLifetime | null;
    replacementDate: Date | null;
    hasCondition: boolean;
    hasLifetime: boolean;
    yearsRemaining: number | null;
}

interface GroupedComponents {
    [baseType: string]: ComponentWithReplacement[];
}

// New interfaces for the nested structure
interface MaintenanceActivity {
    nextDate: string;
    intervalType: MaintenanceIntervalType;
    intervalValue: number;
    cost?: number;
    suggestedCost?: number;
    costCalculation?: string;
    lastUpdated: string;
    disabled?: boolean;
}

interface ComponentMaintenanceData {
    replacementDate?: number;
    replacementCost?: number;
    suggestedReplacementCost?: number;
    replacementCostCalculation?: string;
    isDone?: boolean;
    maintenanceActivities: {
        [templateId: string]: MaintenanceActivity;
    };
}

interface MaintenanceDraft {
    components: {
        [componentKey: string]: ComponentMaintenanceData;
    };
    metadata: {
        savedAt: string;
        buildingKey: string;
    };
    rejectionComment?: {
        comment: string;
        rejectedBy: string;
        rejectedAt: string;
    };
}

interface MaintenanceProposal {
    components: {
        [componentKey: string]: ComponentMaintenanceData;
    };
    metadata: {
        savedAt: string;
        buildingKey: string;
    };
    submission: {
        submittedBy: string;
        submittedAt: string;
        status: 'pending' | 'approved' | 'rejected';
        reviewedBy?: string;
        reviewedAt?: string;
        rejectionComment?: string;
    };
}


// Helper function to calculate component area from area shapes
const calculateComponentArea = (component: BaseComponentImpl): number => {
    if (!component.areaShapes?.value || !Array.isArray(component.areaShapes.value)) {
        return 0;
    }
    
    return component.areaShapes.value.reduce((total, shape) => {
        return total + computeShapeArea(shape);
    }, 0);
};

// Helper function to calculate suggested replacement cost
const calculateSuggestedReplacementCost = (
    component: BaseComponentImpl, 
    lifetime: ComponentLifetime | null
): { cost: number; calculation: string } => {
    if (!lifetime) {
        return { cost: 0, calculation: '' };
    }

    // Get the appropriate lifetime data (considering directional lifetimes)
    const directionalLifetime = getLifetimeForDirection(lifetime, component.cardinalDirection?.value || null);

    if (!directionalLifetime.replacementCost) {
        return { cost: 0, calculation: '' };
    }

    if (lifetime.costUnit === 'per_sqm') {
        const area = calculateComponentArea(component);
        
        if (area === 0) {
            return { 
                cost: 0, 
                calculation: 'Antal kvadratmeter saknas för komponent' 
            };
        }
        const totalCost = directionalLifetime.replacementCost * area;
        return { 
            cost: totalCost, 
            calculation: `${directionalLifetime.replacementCost} kr per kvm * ${area} kvm` 
        };
    } else {
        return { 
            cost: directionalLifetime.replacementCost, 
            calculation: `${directionalLifetime.replacementCost} kr per enhet` 
        };
    }
};

// Helper function to calculate suggested maintenance cost
const calculateSuggestedMaintenanceCost = (
    component: BaseComponentImpl,
    template: MaintenanceTemplate
): { cost: number; calculation: string } => {
    if (!template.cost) {
        return { cost: 0, calculation: '' };
    }

    if (template.cost_unit === 'per_sqm') {
        const area = calculateComponentArea(component);
        
        if (area === 0) {
            return { 
                cost: 0, 
                calculation: 'Antal kvadratmeter saknas för komponent' 
            };
        }
        const totalCost = template.cost * area;
        return { 
            cost: totalCost, 
            calculation: `${template.cost} kr per kvm * ${area} kvm` 
        };
    } else {
        return { 
            cost: template.cost, 
            calculation: `${template.cost} kr per enhet` 
        };
    }
};

const MaintenanceDraft: React.FC = () => {
    const { buildingKey } = useParams<{ buildingKey: string }>();
    const navigate = useNavigate();
    const { userNames, loadAllUsers } = useUserNames();
    const [building, setBuilding] = useState<Building | null>(null);
    const [componentLifetimes, setComponentLifetimes] = useState<Record<string, ComponentLifetime>>({});
    const [groupedComponents, setGroupedComponents] = useState<GroupedComponents>({});
    const [groupedIncompleteComponents, setGroupedIncompleteComponents] = useState<GroupedComponents>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
    const [maintenanceDraft, setMaintenanceDraft] = useState<MaintenanceDraft>({
        components: {},
        metadata: {
            savedAt: '',
            buildingKey: buildingKey || ''
        }
    });
    const [suggestedReplacementDates, setSuggestedReplacementDates] = useState<Record<string, number>>({});
    const [suggestedReplacementCosts, setSuggestedReplacementCosts] = useState<Record<string, { cost: number; calculation: string }>>({});
    const [componentConditions, setComponentConditions] = useState<Record<string, any>>({});
    const [maintenanceTemplates, setMaintenanceTemplates] = useState<TemplatesByType>({});
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
    const [autosaveTimeout, setAutosaveTimeout] = useState<NodeJS.Timeout | null>(null);
    const [proposedPlan, setProposedPlan] = useState<MaintenanceProposal | null>(null);
    const [withdrawing, setWithdrawing] = useState<boolean>(false);
    const [highlightedActivity, setHighlightedActivity] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);
    const [dataFullyLoaded, setDataFullyLoaded] = useState<boolean>(false);
    const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
    const [conditionAssessment, setConditionAssessment] = useState<ConditionAssessmentSettings>(DEFAULT_CONDITION_ASSESSMENT_SETTINGS);

    const db = getDatabase();

    // Validate that all necessary data is fully loaded
    const validateDataLoaded = (): boolean => {
        const hasBuilding = !!(building && building.model && building.model.structure && building.model.structure.components);
        const hasComponentLifetimes = Object.keys(componentLifetimes).length > 0;
        const hasMaintenanceTemplates = Object.keys(maintenanceTemplates).length > 0;
        const hasComponentConditions = componentConditions !== null; // Can be empty object
        const hasDraftData = !!(maintenanceDraft && maintenanceDraft.components !== undefined);
        const hasProcessedComponents = Object.keys(groupedComponents).length > 0 || Object.keys(groupedIncompleteComponents).length > 0;

        return hasBuilding && hasComponentLifetimes && hasMaintenanceTemplates && hasComponentConditions && hasDraftData && hasProcessedComponents && !loading;
    };

    // Load proposed plan and user names
    const loadProposedPlanAndUsers = async (): Promise<void> => {
        if (!buildingKey) return;

        try {
            const proposedSnapshot = await get(ref(db, `maintenance/proposed/${buildingKey}`));

            // Load proposed plan
            if (proposedSnapshot.exists()) {
                const proposedData = proposedSnapshot.val() as MaintenanceProposal;
                setProposedPlan(proposedData);
            } else {
                setProposedPlan(null);
            }

            // Load user names
            await loadAllUsers();
        } catch (error) {
            console.error('Error loading proposed plan and users:', error);
        }
    };

    // Withdraw proposed plan
    const handleWithdrawProposal = async (): Promise<void> => {
        if (!buildingKey || !proposedPlan) return;

        setWithdrawing(true);
        try {
            // Move proposed plan back to drafts
            const draftData: MaintenanceDraft = {
                components: proposedPlan.components,
                metadata: proposedPlan.metadata
            };

            // First, save to drafts
            await set(ref(db, `maintenance/drafts/${buildingKey}`), draftData);
            
            // Verify the draft was saved successfully
            const draftSnapshot = await get(ref(db, `maintenance/drafts/${buildingKey}`));
            if (!draftSnapshot.exists()) {
                throw new Error('Failed to save draft - verification failed');
            }

            // Only after successful verification, remove from proposed
            await set(ref(db, `maintenance/proposed/${buildingKey}`), null);

            // Reload data
            await loadMaintenancePlanDraft();
            await loadProposedPlanAndUsers();

        } catch (error) {
            console.error('Error withdrawing proposal:', error);
            alert('Ett fel uppstod vid återkallande av förslaget. Försök igen.');
        } finally {
            setWithdrawing(false);
        }
    };

    // Show delete draft modal
    const handleDeleteDraft = () => {
        setShowDeleteModal(true);
    };

    // Confirm delete draft
    const handleConfirmDeleteDraft = async (): Promise<void> => {
        if (!buildingKey) return;

        setDeleting(true);
        try {
            // Delete the draft
            await set(ref(db, `maintenance/drafts/${buildingKey}`), null);
            
            // Navigate back to building page
            navigate(`/building/${buildingKey}`);
        } catch (error) {
            console.error('Error deleting draft:', error);
            alert('Ett fel uppstod vid radering av utkastet. Försök igen.');
        } finally {
            setDeleting(false);
            setShowDeleteModal(false);
        }
    };

    // Cancel delete draft
    const handleCancelDeleteDraft = () => {
        setShowDeleteModal(false);
    };

    // Close tooltip when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            setActiveTooltip(null);
        };

        if (activeTooltip) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [activeTooltip]);

    // Cleanup autosave timeout on unmount
    useEffect(() => {
        return () => {
            if (autosaveTimeout) {
                clearTimeout(autosaveTimeout);
            }
        };
    }, [autosaveTimeout]);

    // Load initial data when component mounts
    useEffect(() => {
        const loadInitialData = async () => {
            await Promise.all([
                loadBuilding(),
                loadComponentLifetimes(),
                loadMaintenanceTemplates(),
                loadComponentConditions(),
                loadMaintenancePlanDraft(),
                loadProposedPlanAndUsers(),
                loadConditionAssessmentSettings()
            ]);
            setLoading(false);
        };

        loadInitialData();
    }, [buildingKey]);

    // Load building data
    const loadBuilding = async (): Promise<void> => {
        try {
            const [summarySnapshot, modelSnapshot] = await Promise.all([
                get(ref(db, `buildingsSummary/${buildingKey!}`)),
                get(ref(db, `buildings/${buildingKey!}`))
            ]);

            if (summarySnapshot.exists() || modelSnapshot.exists()) {
                const building: Building = {
                    key: buildingKey!,
                    summary: summarySnapshot.exists() ? summarySnapshot.val() : { key: buildingKey! },
                    model: modelSnapshot.exists() ? modelSnapshot.val() : { key: buildingKey! }
                };
                setBuilding(building);
            }
        } catch (error) {
            console.error('Error loading building:', error);
        }
    };

    // Load component lifetimes
    const loadComponentLifetimes = async (): Promise<void> => {
        try {
            const lifetimesRef = ref(db, 'maintenance/componentLifetimes');
            const snapshot = await get(lifetimesRef);
            
            if (snapshot.exists()) {
                const data = snapshot.val() as Record<string, ComponentLifetime>;
                setComponentLifetimes(data);
            } else {
                setComponentLifetimes({});
            }
        } catch (error) {
            console.error('Error loading component lifetimes:', error);
            setComponentLifetimes({});
        }
    };

    // Load maintenance templates
    const loadMaintenanceTemplates = async (): Promise<void> => {
        try {
            const templatesRef = ref(db, 'maintenance/templates');
            const snapshot = await get(templatesRef);

            const templatesByType: TemplatesByType = {};

            if (snapshot.exists()) {
                const data = snapshot.val() as Record<string, Record<string, any>>;
                
                Object.entries(data).forEach(([componentType, componentTemplates]) => {
                    if (componentTemplates && typeof componentTemplates === 'object') {
                        templatesByType[componentType] = Object.entries(componentTemplates).map(([id, template]: [string, any]) => {
                            const processedTemplate = {
                                id,
                                name: template.name || '',
                                description: template.description || '',
                                interval_type: template.interval_type || 'years',
                                interval_value: template.interval_value || 1,
                                cost: template.cost ?? 0,
                                cost_unit: template.cost_unit ?? 'per_sqm',
                                directionalIntervals: template.directionalIntervals || undefined
                            };
                            
                            
                            return processedTemplate;
                        });
                    }
                });
            }

            setMaintenanceTemplates(templatesByType);
        } catch (error) {
            console.error('Error loading maintenance templates:', error);
            setMaintenanceTemplates({});
        }
    };

    // Load component conditions
    const loadComponentConditions = async (): Promise<void> => {
        try {
            const conditionsRef = ref(db, `componentConditions/${buildingKey!}`);
            const snapshot = await get(conditionsRef);
            
            if (snapshot.exists()) {
                const data = snapshot.val() as Record<string, any>;
                setComponentConditions(data);
            } else {
                setComponentConditions({});
            }
        } catch (error) {
            console.error('Error loading component conditions:', error);
            setComponentConditions({});
        }
    };

    // Load condition assessment settings
    const loadConditionAssessmentSettings = async (): Promise<void> => {
        try {
            const loadedSettings: Partial<ConditionAssessmentSettings> = {};

            // Load each category
            for (const category of CONDITION_ASSESSMENT_CATEGORIES) {
                const categoryKey = category.key as keyof ConditionAssessmentSettings;
                const categoryRef = ref(db, `maintenance/assessmentSettings/${categoryKey}`);
                const snapshot = await get(categoryRef);

                if (snapshot.exists()) {
                    loadedSettings[categoryKey] = snapshot.val();
                }
            }

            // Merge loaded settings with defaults
            setConditionAssessment({
                roofAndDrainage: loadedSettings.roofAndDrainage || DEFAULT_CONDITION_ASSESSMENT_SETTINGS.roofAndDrainage,
                facadeAndWindows: loadedSettings.facadeAndWindows || DEFAULT_CONDITION_ASSESSMENT_SETTINGS.facadeAndWindows,
                surfaces: loadedSettings.surfaces || DEFAULT_CONDITION_ASSESSMENT_SETTINGS.surfaces
            });
        } catch (error) {
            console.error('Error loading condition assessment settings:', error);
        }
    };

    // Get assessment settings for a group
    const getAssessmentForGroup = (groupKey: string): ConditionAssessmentCategory | null => {
        const mapping: Record<string, keyof ConditionAssessmentSettings> = {
            'roof_drainage': 'roofAndDrainage',
            'facade-windows': 'facadeAndWindows'
        };

        const assessmentKey = mapping[groupKey];
        return assessmentKey ? conditionAssessment[assessmentKey] : null;
    };

    // Calculate next assessment date based on current date + interval
    const calculateNextAssessmentDate = (assessment: ConditionAssessmentCategory): string => {
        const now = new Date();
        const { value, unit } = assessment.interval;
        
        let nextDate = new Date(now);
        
        switch (unit) {
            case 'days':
                nextDate.setDate(now.getDate() + value);
                break;
            case 'weeks':
                nextDate.setDate(now.getDate() + (value * 7));
                break;
            case 'months':
                nextDate.setMonth(now.getMonth() + value);
                break;
            case 'years':
                nextDate.setFullYear(now.getFullYear() + value);
                break;
        }
        
        return formatDateToString(nextDate);
    };

    // Calculate replacement date based on condition
    const calculateReplacementDate = (component: BaseComponentImpl, lifetime: ComponentLifetime): Date | null => {
        if (!component.condition?.value || !component.condition.updatedWhen || !lifetime.expectedLifetimeYears) {
            return null;
        }

        const condition = component.condition.value;
        const assessmentDate = new Date(component.condition.updatedWhen);
        
        // Use directional lifetime if available
        const directionalLifetime = getLifetimeForDirection(lifetime, component.cardinalDirection?.value || null);
        const expectedLifetime = directionalLifetime.expectedLifetimeYears;

        // Calculate years remaining based on condition (10 = full lifetime, 0 = replace now)
        const yearsRemainingFromCondition = (condition / 10) * expectedLifetime;

        // Calculate years since assessment
        const now = new Date();
        const yearsSinceAssessment = (now.getTime() - assessmentDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);

        // Adjust for time passed since assessment
        const adjustedYearsRemaining = yearsRemainingFromCondition - yearsSinceAssessment;

        const replacementDate = new Date();
        replacementDate.setFullYear(replacementDate.getFullYear() + Math.max(0, adjustedYearsRemaining));

        return replacementDate;
    };

    // Calculate years remaining
    const calculateYearsRemaining = (component: BaseComponentImpl, lifetime: ComponentLifetime): number | null => {
        if (!component.condition?.value || !lifetime.expectedLifetimeYears) {
            return null;
        }

        const condition = component.condition.value;
        
        // Use directional lifetime if available
        const directionalLifetime = getLifetimeForDirection(lifetime, component.cardinalDirection?.value || null);
        const expectedLifetime = directionalLifetime.expectedLifetimeYears;

        // Calculate years remaining based on condition (10 = full lifetime, 0 = replace now)
        const yearsRemainingFromCondition = (condition / 10) * expectedLifetime;

        // Adjust for time passed since assessment
        if (component.condition.updatedWhen) {
            const assessmentDate = new Date(component.condition.updatedWhen);
            const now = new Date();
            const yearsSinceAssessment = (now.getTime() - assessmentDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
            return Math.max(0, yearsRemainingFromCondition - yearsSinceAssessment);
        }

        return yearsRemainingFromCondition;
    };

    // Process components and create maintenance plans
    const processComponents = (): void => {
        if (!building?.model?.structure?.components) {
            setGroupedComponents({});
            setGroupedIncompleteComponents({});
            return;
        }

        const grouped: GroupedComponents = {};
        const groupedIncomplete: GroupedComponents = {};

        // Define base types in the same order as templates page
        const baseTypeOrder = RELEVANT_BASE_TYPES;

        // Initialize groups for all BaseTypes
        baseTypeOrder.forEach(baseType => {
            grouped[baseType] = [];
            groupedIncomplete[baseType] = [];
        });

        Object.values(building.model.structure.components || {}).forEach(componentData => {
            if (!componentData.specificType) return;

            // Merge component conditions if available
            const componentKey = componentData.key;
            
            
            const conditionData = componentConditions[componentKey];
            if (conditionData) {
                // Merge condition data into component data
                componentData.condition = conditionData;
            }

            // Convert plain object to BaseComponentImpl instance
            const component = new BaseComponentImpl(componentData);

            const componentType = component.specificType;
            if (!componentType) return;


            const lifetime = componentLifetimes[componentType] || null;
            const hasCondition = component.condition?.value !== undefined && component.condition.value !== null && component.condition.value > 0;
            const hasLifetime = lifetime?.expectedLifetimeYears !== undefined && lifetime.expectedLifetimeYears > 0;
            

            let replacementDate: Date | null = null;
            let yearsRemaining: number | null = null;

            if (hasCondition && hasLifetime) {
                replacementDate = calculateReplacementDate(component, lifetime!);
                yearsRemaining = calculateYearsRemaining(component, lifetime!);
            }

            const componentPlan: ComponentWithReplacement = {
                component,
                lifetime,
                replacementDate,
                hasCondition,
                hasLifetime,
                yearsRemaining
            };


            if (hasCondition && hasLifetime) {
                // Group by BaseType - show replacement info when both condition and lifetime are available
                // This includes components even if no maintenance templates are configured
                const baseType = component.type;
                if (grouped[baseType]) {
                    grouped[baseType].push(componentPlan);
                }
            } else {
                // Group incomplete components by BaseType
                const baseType = component.type;
                if (groupedIncomplete[baseType]) {
                    groupedIncomplete[baseType].push(componentPlan);
                }
            }
        });

        // Sort each group by replacement date (earliest first)
        Object.keys(grouped).forEach(baseType => {
            grouped[baseType].sort((a, b) => {
                if (!a.replacementDate && !b.replacementDate) return 0;
                if (!a.replacementDate) return 1;
                if (!b.replacementDate) return -1;
                return a.replacementDate.getTime() - b.replacementDate.getTime();
            });
        });

        // Sort incomplete components by type within each base type
        Object.keys(groupedIncomplete).forEach(baseType => {
            groupedIncomplete[baseType].sort((a, b) => {
            if (a.component.specificType && b.component.specificType) {
                return a.component.specificType.localeCompare(b.component.specificType);
            }
            return 0;
            });
        });

        setGroupedComponents(grouped);
        setGroupedIncompleteComponents(groupedIncomplete);
    };

    // Calculate suggested years based on calculations
    const calculateSuggestedCosts = (): void => {
        if (!building?.model?.structure?.components || !componentLifetimes || !maintenanceTemplates) {
            return;
        }

        const newSuggestedReplacementCosts: Record<string, { cost: number; calculation: string }> = {};

        const components = Object.values(building.model.structure.components || {});

        components.forEach((componentData) => {
            if (!componentData.specificType) {
                return;
            }

            // Merge component conditions if available
            const componentKey = componentData.key;
            const conditionData = componentConditions[componentKey];
            
            if (conditionData) {
                // Merge condition data into component data
                componentData.condition = conditionData;
            }

            const component = new BaseComponentImpl(componentData);
            const componentType = component.specificType;
            
            if (!componentType) {
                return;
            }

            const lifetime = componentLifetimes[componentType];
            
            const hasCondition = component.condition?.value !== undefined && component.condition.value !== null && component.condition.value > 0;
            const hasLifetime = lifetime?.expectedLifetimeYears !== undefined && lifetime.expectedLifetimeYears > 0;

            if (hasCondition && hasLifetime) {
                // Calculate suggested replacement cost
                const replacementCostData = calculateSuggestedReplacementCost(component, lifetime);
                
                if (replacementCostData.cost > 0) {
                    newSuggestedReplacementCosts[component.key] = replacementCostData;
                }
            }
        });

        setSuggestedReplacementCosts(newSuggestedReplacementCosts);

        // Update the maintenanceDraft state with suggested values for components that don't have user-set values
        setMaintenanceDraft(prevDraft => {
            const updatedComponents = { ...prevDraft.components };
            
            // Process each component directly
            components.forEach((componentData) => {
                if (!componentData.specificType) {
                    return;
                }

                const componentKey = componentData.key;
                const component = new BaseComponentImpl(componentData);
                const componentType = component.specificType;
                
                if (!componentType) {
                    return;
                }

                const lifetime = componentLifetimes[componentType];
                const hasCondition = component.condition?.value !== undefined && component.condition.value !== null && component.condition.value > 0;
                const hasLifetime = lifetime?.expectedLifetimeYears !== undefined && lifetime.expectedLifetimeYears > 0;

                if (hasCondition && hasLifetime) {
                    // Initialize component if it doesn't exist
                    if (!updatedComponents[componentKey]) {
                        updatedComponents[componentKey] = { 
                            maintenanceActivities: {},
                            isDone: false
                        };
                    }
                    
                    // Ensure maintenanceActivities exists even if component already exists
                    if (!updatedComponents[componentKey].maintenanceActivities) {
                        updatedComponents[componentKey].maintenanceActivities = {};
                    }

                    // Update replacement date
                    if (updatedComponents[componentKey].replacementDate === undefined) {
                        const replacementDate = calculateReplacementDate(component, lifetime);
                        if (replacementDate) {
                            updatedComponents[componentKey].replacementDate = replacementDate.getFullYear();
                        }
                    }

                    // Update replacement cost
                    if (updatedComponents[componentKey].replacementCost === undefined) {
                        const replacementCostData = calculateSuggestedReplacementCost(component, lifetime);
                        if (replacementCostData.cost > 0) {
                            updatedComponents[componentKey].replacementCost = replacementCostData.cost;
                            updatedComponents[componentKey].suggestedReplacementCost = replacementCostData.cost;
                            updatedComponents[componentKey].replacementCostCalculation = replacementCostData.calculation;
                        }
                    }

                    // Update maintenance activities
                    const templates = getRelevantTemplates(componentType);
                    if (Array.isArray(templates) && templates.length > 0) {
                        templates.forEach((template) => {
                            if (!template || !template.id) {
                                return;
                            }

                            const templateId = template.id;
                            
                            // Initialize maintenance activity if it doesn't exist
                            if (!updatedComponents[componentKey].maintenanceActivities[templateId]) {
                                // Calculate suggested maintenance year
                                let suggestedYear = new Date().getFullYear();
                                const nextMaintenanceDate = calculateNextMaintenanceDate(component, template, lifetime);
                                if (nextMaintenanceDate) {
                                    suggestedYear = nextMaintenanceDate.getFullYear();
                                }

                                // Get directional interval
                                const defaultInterval = getIntervalForDirection(template, component.cardinalDirection?.value || null);

                                updatedComponents[componentKey].maintenanceActivities[templateId] = {
                                    nextDate: formatDateToString(nextMaintenanceDate || new Date()),
                                    intervalType: defaultInterval.interval_type,
                                    intervalValue: defaultInterval.interval_value,
                                    lastUpdated: new Date().toISOString()
                                };
                            }

                            // Update maintenance cost
                            if (updatedComponents[componentKey].maintenanceActivities[templateId].cost === undefined) {
                                const maintenanceCostData = calculateSuggestedMaintenanceCost(component, template);
                                if (maintenanceCostData.cost > 0) {
                                    updatedComponents[componentKey].maintenanceActivities[templateId].cost = maintenanceCostData.cost;
                                    updatedComponents[componentKey].maintenanceActivities[templateId].suggestedCost = maintenanceCostData.cost;
                                    updatedComponents[componentKey].maintenanceActivities[templateId].costCalculation = maintenanceCostData.calculation;
                                }
                            }
                        });
                    }
                }
            });

            const updatedDraft = {
                ...prevDraft,
                components: updatedComponents
            };

            // Auto-save the draft with all suggested values after a short delay
            setTimeout(() => {
                console.log('Auto-saving initial draft with suggested values...');
                autosaveDraft(updatedDraft);
            }, 1000);

            return updatedDraft;
        });
    };


    const toggleSection = (sectionKey: string): void => {
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(sectionKey)) {
            newExpanded.delete(sectionKey);
        } else {
            newExpanded.add(sectionKey);
        }
        setExpandedSections(newExpanded);
    };

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([
                loadBuilding(),
                loadComponentLifetimes(),
                loadMaintenanceTemplates(),
                loadComponentConditions(),
                loadMaintenancePlanDraft(),
                loadProposedPlanAndUsers()
            ]);
            setLoading(false);
        };

        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildingKey]);


    useEffect(() => {
        if (!loading) {
            processComponents();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [building, componentLifetimes, componentConditions, loading]);

    // Update dataFullyLoaded state whenever validation status changes
    useEffect(() => {
        const isDataLoaded = validateDataLoaded();
        setDataFullyLoaded(isDataLoaded);
        
        if (isDataLoaded) {
            console.log('✅ All maintenance draft data fully loaded');
        } else {
            console.log('⏳ Waiting for maintenance draft data to load...');
        }
    }, [building, componentLifetimes, maintenanceTemplates, componentConditions, maintenanceDraft, groupedComponents, groupedIncompleteComponents, loading]);

    // Calculate suggested years and costs when all required data is available
    useEffect(() => {
        if (building?.model?.structure?.components && 
            Object.keys(componentLifetimes).length > 0 && 
            Object.keys(maintenanceTemplates).length > 0) {
            calculateSuggestedCosts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [building, componentLifetimes, maintenanceTemplates, componentConditions]);

    // Early return if buildingKey is not available (after all hooks)
    if (!buildingKey) {
        return (
            <div className="alert alert-warning">
                <h4>Byggnadsnyckel saknas</h4>
                <p>URL:en måste innehålla en byggnadsnyckel. Använd formatet: /maintenanceDrafts/byggnadsnyckel</p>
            </div>
        );
    }

    const formatDate = (date: Date): string => {
        return date.toLocaleDateString('sv-SE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatYears = (years: number): string => {
        if (years < 1) {
            const months = Math.round(years * 12);
            return `${months} månader`;
        }
        if (years === 1) {
            return '1 år';
        }
        return `${Math.round(years)} år`;
    };

    const getConditionColor = (condition: number): string => {
        if (condition >= 8) return '#28a745'; // Green
        if (condition >= 6) return '#ffc107'; // Yellow
        if (condition >= 4) return '#fd7e14'; // Orange
        return '#dc3545'; // Red
    };

    const getReplacementPriority = (yearsRemaining: number): { color: string; text: string } => {
        if (yearsRemaining <= 1) return { color: '#dc3545', text: 'Hög prioritet' };
        if (yearsRemaining <= 3) return { color: '#fd7e14', text: 'Medel prioritet' };
        if (yearsRemaining <= 5) return { color: '#ffc107', text: 'Låg prioritet' };
        return { color: '#28a745', text: 'Långt fram' };
    };

    const handleReplacementDateChange = (componentKey: string, newDate: string) => {
        const year = parseInt(newDate, 10);
        setMaintenanceDraft(prev => ({
            ...prev,
            components: {
                ...prev.components,
                [componentKey]: {
                    ...prev.components[componentKey],
                    replacementDate: isNaN(year) ? undefined : year,
                    maintenanceActivities: prev.components[componentKey]?.maintenanceActivities || {}
                }
            }
        }));
    };

    const handleReplacementDateBlur = (componentKey: string) => {
        scheduleAutosave();
    };

    const handleMaintenanceDateChange = (componentKey: string, templateId: string, newDate: string) => {
        setMaintenanceDraft(prev => ({
            ...prev,
            components: {
                ...prev.components,
                [componentKey]: {
                    ...prev.components[componentKey],
                    replacementDate: prev.components[componentKey]?.replacementDate,
                    maintenanceActivities: {
                        ...prev.components[componentKey]?.maintenanceActivities,
                        [templateId]: {
                            ...prev.components[componentKey]?.maintenanceActivities?.[templateId],
                            nextDate: newDate || formatDateToString(new Date()),
                            intervalType: prev.components[componentKey]?.maintenanceActivities?.[templateId]?.intervalType || MaintenanceIntervalType.years,
                            intervalValue: prev.components[componentKey]?.maintenanceActivities?.[templateId]?.intervalValue || 1,
                            lastUpdated: new Date().toISOString()
                        }
                    }
                }
            }
        }));
    };

    const handleMaintenanceDateBlur = (componentKey: string, templateId: string) => {
        scheduleAutosave();
    };

    const handleMaintenanceIntervalChange = (componentKey: string, templateId: string, newInterval: string) => {
        const interval = parseInt(newInterval, 10);
        
        setMaintenanceDraft(prev => {
            // Preserve existing intervalType or get from template
            const existingActivity = prev.components[componentKey]?.maintenanceActivities?.[templateId];
            let intervalType = existingActivity?.intervalType;
            
            // Only determine intervalType from template if not already set
            if (!intervalType) {
                const componentData = Object.values(building?.model?.structure?.components || {}).find(c => c.key === componentKey);
                const component = componentData ? new BaseComponentImpl(componentData) : null;
                const componentType = component?.specificType;
                const templates = componentType ? maintenanceTemplates[componentType] || [] : [];
                const template = templates.find(t => t.id === templateId);
                
                intervalType = MaintenanceIntervalType.years;
                if (template && component) {
                    const directionalInterval = getIntervalForDirection(template, component.cardinalDirection?.value || null);
                    intervalType = directionalInterval.interval_type;
                } else if (template) {
                    intervalType = template.interval_type;
                }
            }
            
            return {
                ...prev,
                components: {
                    ...prev.components,
                    [componentKey]: {
                        ...prev.components[componentKey],
                        replacementDate: prev.components[componentKey]?.replacementDate,
                        maintenanceActivities: {
                            ...prev.components[componentKey]?.maintenanceActivities,
                            [templateId]: {
                                ...existingActivity,
                                nextDate: existingActivity?.nextDate || formatDateToString(new Date()),
                                intervalType: intervalType,
                                intervalValue: isNaN(interval) ? 1 : interval,
                                lastUpdated: new Date().toISOString()
                            }
                        }
                    }
                }
            };
        });
    };

    const handleMaintenanceIntervalBlur = (componentKey: string, templateId: string) => {
        scheduleAutosave();
    };

    const handleMaintenanceIntervalTypeChange = (componentKey: string, templateId: string, newType: MaintenanceIntervalType) => {
        setMaintenanceDraft(prev => ({
            ...prev,
            components: {
                ...prev.components,
                [componentKey]: {
                    ...prev.components[componentKey],
                    replacementDate: prev.components[componentKey]?.replacementDate,
                    maintenanceActivities: {
                        ...prev.components[componentKey]?.maintenanceActivities,
                        [templateId]: {
                            ...prev.components[componentKey]?.maintenanceActivities?.[templateId],
                            nextDate: prev.components[componentKey]?.maintenanceActivities?.[templateId]?.nextDate || formatDateToString(new Date()),
                            intervalType: newType,
                            intervalValue: prev.components[componentKey]?.maintenanceActivities?.[templateId]?.intervalValue || 1,
                            lastUpdated: new Date().toISOString()
                        }
                    }
                }
            }
        }));
        scheduleAutosave();
    };

    const handleReplacementCostChange = (componentKey: string, value: string): void => {
        const cost = value === '' ? undefined : parseFloat(value);
        
        setMaintenanceDraft(prev => ({
            ...prev,
            components: {
                ...prev.components,
                [componentKey]: {
                    ...prev.components[componentKey],
                    replacementCost: cost
                }
            }
        }));
    };

    const handleReplacementCostBlur = (componentKey: string) => {
        scheduleAutosave();
    };

    const handleMaintenanceCostChange = (componentKey: string, templateId: string, value: string): void => {
        const cost = value === '' ? undefined : parseFloat(value);
        
        setMaintenanceDraft(prev => ({
            ...prev,
            components: {
                ...prev.components,
                [componentKey]: {
                    ...prev.components[componentKey],
                    maintenanceActivities: {
                        ...prev.components[componentKey]?.maintenanceActivities,
                        [templateId]: {
                            ...prev.components[componentKey]?.maintenanceActivities?.[templateId],
                            cost: cost,
                            lastUpdated: new Date().toISOString()
                        }
                    }
                }
            }
        }));
    };

    const handleMaintenanceCostBlur = (componentKey: string, templateId: string) => {
        scheduleAutosave();
    };

    const handleMaintenanceActivityToggle = (componentKey: string, templateId: string): void => {
        setMaintenanceDraft(prev => {
            const updatedDraft = {
                ...prev,
                components: {
                    ...prev.components,
                    [componentKey]: {
                        ...prev.components[componentKey],
                        maintenanceActivities: {
                            ...prev.components[componentKey]?.maintenanceActivities,
                            [templateId]: {
                                ...prev.components[componentKey]?.maintenanceActivities?.[templateId],
                                disabled: !prev.components[componentKey]?.maintenanceActivities?.[templateId]?.disabled,
                                lastUpdated: new Date().toISOString()
                            }
                        }
                    }
                }
            };
            
            // Schedule autosave with the updated draft
            scheduleAutosave(updatedDraft);
            
            return updatedDraft;
        });
    };

    const handleDoneToggle = (componentKey: string): void => {
        setMaintenanceDraft(prev => {
            const currentComponent = prev.components[componentKey] || { 
                maintenanceActivities: {},
                isDone: false
            };
            const newIsDone = !currentComponent.isDone;
            
            console.log(`Toggling done state for ${componentKey}: ${currentComponent.isDone} -> ${newIsDone}`);
            
            const updatedDraft = {
                ...prev,
                components: {
                    ...prev.components,
                    [componentKey]: {
                        ...currentComponent,
                        isDone: newIsDone
                    }
                }
            };
            
            // Schedule autosave with the updated draft
            scheduleAutosave(updatedDraft);
            
            return updatedDraft;
        });
    };

    const getDisplayReplacementDate = (plan: ComponentWithReplacement): string => {
        const componentKey = plan.component.key;
        if (maintenanceDraft.components[componentKey]?.replacementDate !== undefined) {
            return maintenanceDraft.components[componentKey].replacementDate!.toString();
        }
        if (suggestedReplacementDates[componentKey]) {
            return suggestedReplacementDates[componentKey].toString();
        }
        if (plan.replacementDate) {
            return plan.replacementDate.getFullYear().toString();
        }
        return '';
    };

    const getDisplayMaintenanceDate = (componentKey: string, templateId: string, calculatedDate: Date | null): string => {
        const activity = maintenanceDraft.components[componentKey]?.maintenanceActivities?.[templateId];
        if (activity?.nextDate) {
            return activity.nextDate; // Already in YYYY-MM-DD format
        }
        if (calculatedDate) {
            return formatDateToString(calculatedDate);
        }
        return '';
    };

    const getDisplayMaintenanceInterval = (componentKey: string, templateId: string, template: MaintenanceTemplate): string => {
        const activity = maintenanceDraft.components[componentKey]?.maintenanceActivities?.[templateId];
        if (activity?.intervalValue !== undefined) {
            return activity.intervalValue.toString();
        }
        
        // Use directional interval if available
        const componentData = Object.values(building?.model?.structure?.components || {}).find(c => c.key === componentKey);
        if (componentData) {
            const component = new BaseComponentImpl(componentData);
            const directionalInterval = getIntervalForDirection(template, component.cardinalDirection?.value || null);
            return directionalInterval.interval_value.toString();
        }
        
        return template.interval_value.toString();
    };

    const getDisplayBaseInterval = (componentKey: string, template: MaintenanceTemplate): { value: string, type: MaintenanceIntervalType } => {
        const componentData = Object.values(building?.model?.structure?.components || {}).find(c => c.key === componentKey);
        if (componentData) {
            const component = new BaseComponentImpl(componentData);
            const directionalInterval = getIntervalForDirection(template, component.cardinalDirection?.value || null);
            
            return {
                value: directionalInterval.interval_value.toString(),
                type: directionalInterval.interval_type
            };
        }
        return {
            value: template.interval_value.toString(),
            type: template.interval_type
        };
    };

    const getDisplayLifetime = (componentKey: string, lifetime: ComponentLifetime): string => {
        const componentData = Object.values(building?.model?.structure?.components || {}).find(c => c.key === componentKey);
        if (componentData) {
            const component = new BaseComponentImpl(componentData);
            const directionalLifetime = getLifetimeForDirection(lifetime, component.cardinalDirection?.value || null);
            return directionalLifetime.expectedLifetimeYears.toString();
        }
        return lifetime.expectedLifetimeYears.toString();
    };

    const getDisplayReplacementCost = (componentKey: string): string => {
        const cost = maintenanceDraft.components[componentKey]?.replacementCost;
        if (cost !== undefined) {
            return cost.toString();
        }
        // Use suggested cost if no user value is set
        const suggestedCost = suggestedReplacementCosts[componentKey];
        if (suggestedCost) {
            return suggestedCost.cost.toString();
        }
        return '';
    };

    const getDisplayMaintenanceCost = (componentKey: string, templateId: string): string => {
        const cost = maintenanceDraft.components[componentKey]?.maintenanceActivities?.[templateId]?.cost;
        if (cost !== undefined) {
            return cost.toString();
        }
        // Use suggested cost if no user value is set
        const suggestedCost = maintenanceDraft.components[componentKey]?.maintenanceActivities?.[templateId]?.suggestedCost;
        if (suggestedCost !== undefined) {
            return suggestedCost.toString();
        }
        return '';
    };

    const getReplacementCostCalculation = (componentKey: string): string => {
        // Always show the suggested calculation if available
        const suggestedCost = suggestedReplacementCosts[componentKey];
        if (suggestedCost) {
            return suggestedCost.calculation;
        }
        // Fallback to saved calculation if no suggested calculation
        const calculation = maintenanceDraft.components[componentKey]?.replacementCostCalculation;
        if (calculation) {
            return calculation;
        }
        return '';
    };

    const getMaintenanceCostCalculation = (componentKey: string, templateId: string): string => {
        // Always show the suggested calculation if available
        const calculation = maintenanceDraft.components[componentKey]?.maintenanceActivities?.[templateId]?.costCalculation;
        if (calculation) {
            return calculation;
        }
        return '';
    };

    const isMaintenanceActivityDisabled = (componentKey: string, templateId: string): boolean => {
        return maintenanceDraft.components[componentKey]?.maintenanceActivities?.[templateId]?.disabled === true;
    };

    const getReplacementCostExplanation = (componentKey: string): string => {
        const component = building?.model?.structure?.components?.[componentKey];
        if (!component) {
            return 'Komponent hittades inte';
        }

        const componentImpl = new BaseComponentImpl(component);
        const componentType = componentImpl.specificType;
        
        if (!componentType) {
            return 'Komponenttyp saknas';
        }

        const lifetime = componentLifetimes[componentType];
        const hasCondition = componentImpl.condition?.value !== undefined && componentImpl.condition.value !== null && componentImpl.condition.value > 0;
        const hasLifetime = lifetime?.expectedLifetimeYears !== undefined && lifetime.expectedLifetimeYears > 0;

        if (!hasCondition) {
            return 'Ingen skickbedömning tillgänglig - kostnad kan inte beräknas';
        }

        if (!hasLifetime) {
            return `Ingen livslängdsdata tillgänglig för ${componentType} - kostnad kan inte beräknas`;
        }

        if (!lifetime.replacementCost) {
            return `Ingen ersättningskostnad angiven för ${componentType}`;
        }

        return 'Kostnad kunde inte beräknas - okänt fel';
    };

    const getMaintenanceCostExplanation = (componentKey: string, templateId: string): string => {
        const component = building?.model?.structure?.components?.[componentKey];
        if (!component) {
            return 'Komponent hittades inte';
        }

        const componentImpl = new BaseComponentImpl(component);
        const componentType = componentImpl.specificType;
        
        if (!componentType) {
            return 'Komponenttyp saknas';
        }

        const templates = getRelevantTemplates(componentType);
        const template = templates.find(t => t.id === templateId);
        
        if (!template) {
            return `Underhållsmall hittades inte för ${componentType}`;
        }

        const hasCondition = componentImpl.condition?.value !== undefined && componentImpl.condition.value !== null && componentImpl.condition.value > 0;
        const lifetime = componentLifetimes[componentType];
        const hasLifetime = lifetime?.expectedLifetimeYears !== undefined && lifetime.expectedLifetimeYears > 0;

        if (!hasCondition) {
            return 'Ingen skickbedömning tillgänglig - kostnad kan inte beräknas';
        }

        if (!hasLifetime) {
            return `Ingen livslängdsdata tillgänglig för ${componentType} - kostnad kan inte beräknas`;
        }

        if (!template.cost) {
            return `Ingen kostnad angiven i underhållsmallen "${template.name}"`;
        }

        return 'Kostnad kunde inte beräknas - okänt fel';
    };


    // Validate year value
    const isValidYear = (year: number): boolean => {
        const currentYear = new Date().getFullYear();
        const maxYear = currentYear + 150;
        return year >= currentYear && year <= maxYear;
    };

    // Validate interval value
    const isValidInterval = (interval: number): boolean => {
        return interval > 0 && interval <= 100;
    };

    // Check if a replacement date field is invalid
    const isReplacementDateInvalid = (componentKey: string): boolean => {
        const replacementDate = maintenanceDraft.components[componentKey]?.replacementDate;
        return replacementDate !== undefined && !isValidYear(replacementDate);
    };

    // Check if a maintenance date field is invalid
    const isMaintenanceDateInvalid = (componentKey: string, templateId: string): boolean => {
        const activity = maintenanceDraft.components[componentKey]?.maintenanceActivities?.[templateId];
        const nextDate = activity?.nextDate;
        if (nextDate) {
            try {
                const date = new Date(nextDate);
                return isNaN(date.getTime());
            } catch {
                return true;
            }
        }
        return false;
    };

    // Check if a maintenance interval field is invalid
    const isMaintenanceIntervalInvalid = (componentKey: string, templateId: string): boolean => {
        const activity = maintenanceDraft.components[componentKey]?.maintenanceActivities?.[templateId];
        return activity?.intervalValue !== undefined && !isValidInterval(activity.intervalValue);
    };

    // Check if component has valid saved values
    const hasValidSavedValues = (componentKey: string): boolean => {
        const componentData = maintenanceDraft.components[componentKey];
        if (!componentData) return false;

        // Check replacement date
        if (componentData.replacementDate && !isValidYear(componentData.replacementDate)) {
            return false;
        }

        // Check maintenance years
        if (componentData.maintenanceActivities) {
            for (const activity of Object.values(componentData.maintenanceActivities)) {
                if (activity.nextDate && !isValidYear(new Date(activity.nextDate).getFullYear())) {
                    return false;
                }
            }
        }

        return true;
    };

    // Count done components in a list
    const countDoneComponents = (components: ComponentWithReplacement[]): number => {
        return components.filter(plan => {
            const componentKey = plan.component.key;
            return maintenanceDraft.components[componentKey]?.isDone === true;
        }).length;
    };

    // Calculate next maintenance due date based on condition progression
    const calculateNextMaintenanceDate = (component: BaseComponentImpl, template: MaintenanceTemplate, lifetime: ComponentLifetime): Date | null => {
        if (!component.condition?.value || !component.condition.updatedWhen || !lifetime.expectedLifetimeYears) {
            return null;
        }

        const condition = component.condition.value;
        const expectedLifetime = lifetime.expectedLifetimeYears;
        // Use directional interval if available
        const intervalData = getIntervalForDirection(template, component.cardinalDirection?.value || null);
        const maintenanceIntervalYears = intervalData.interval_type === MaintenanceIntervalType.years 
            ? intervalData.interval_value 
            : intervalData.interval_value / 365; // Convert days to years for calculation

        // Calculate how much of the lifetime has passed based on condition
        // Condition 10 = 0% passed, Condition 0 = 100% passed
        const lifetimePassedPercent = (10 - condition) / 10;
        const yearsPassed = lifetimePassedPercent * expectedLifetime;

        // Calculate when the last maintenance should have been done
        const lastMaintenanceYear = Math.floor(yearsPassed / maintenanceIntervalYears) * maintenanceIntervalYears;
        
        // Calculate next maintenance due date
        const nextMaintenanceYear = lastMaintenanceYear + maintenanceIntervalYears;
        const yearsUntilNextMaintenance = nextMaintenanceYear - yearsPassed;

        // Create date based on assessment date + years until next maintenance
        const assessmentDate = new Date(component.condition.updatedWhen);
        const nextMaintenanceDate = new Date(assessmentDate);
        nextMaintenanceDate.setFullYear(assessmentDate.getFullYear() + yearsUntilNextMaintenance);

        return nextMaintenanceDate;
    };

    // Get relevant maintenance templates for a component
    const getRelevantTemplates = (componentType: string): MaintenanceTemplate[] => {
        const templates = maintenanceTemplates[componentType];
        
        // Ensure we always return an array
        if (Array.isArray(templates)) {
            return templates;
        }
        return [];
    };


    // Autosave function that saves the entire draft
    const autosaveDraft = async (draftToSave?: MaintenanceDraft): Promise<void> => {
        try {
            setSaveStatus('saving');

            // Use provided draft or current state
            const currentDraft = draftToSave || maintenanceDraft;
            
            // Update metadata
            const updatedDraft: MaintenanceDraft = {
                ...currentDraft,
                metadata: {
                    ...currentDraft.metadata,
                    savedAt: new Date().toISOString(),
                    buildingKey: buildingKey!
                }
            };

            // Save to Firebase (don't update local state to avoid race conditions)
            const draftRef = ref(db, `maintenance/drafts/${buildingKey!}`);
            await set(draftRef, updatedDraft);

            setSaveStatus('saved');
            console.log('Autosaved draft with components:', Object.keys(updatedDraft.components));
            console.log('Done states:', Object.entries(updatedDraft.components).map(([key, comp]) => `${key}: ${comp.isDone}`));
            console.log('Rejection comment in autosaved draft:', updatedDraft.rejectionComment);
            
            setTimeout(() => {
                setSaveStatus('idle');
            }, 1000);
        } catch (error) {
            console.error('Error autosaving draft:', error);
            setSaveStatus('error');
            
            setTimeout(() => {
                setSaveStatus('idle');
            }, 3000);
        }
    };

    // Schedule autosave with debouncing
    const scheduleAutosave = (draftToSave?: MaintenanceDraft): void => {
        if (autosaveTimeout) {
            clearTimeout(autosaveTimeout);
        }
        
        console.log('Scheduling autosave...');
        const timeout = setTimeout(() => {
            console.log('Executing autosave...');
            autosaveDraft(draftToSave);
        }, 1000); // 1 second delay
        
        setAutosaveTimeout(timeout);
    };

    // Save individual component to draft (now used for initialization)
    const saveComponentToDraft = async (componentKey: string): Promise<void> => {
        try {
            setSaveStatus('saving');

            // Validate all fields before saving
            const componentData = maintenanceDraft.components[componentKey];
            if (componentData) {
                // Check replacement date
                if (componentData.replacementDate !== undefined && !isValidYear(componentData.replacementDate)) {
                    setSaveStatus('error');
                    console.error(`Invalid replacement date: ${componentData.replacementDate}`);
                    setTimeout(() => setSaveStatus('idle'), 3000);
                    return;
                }

                // Check maintenance activities
                if (componentData.maintenanceActivities) {
                    for (const [templateId, activity] of Object.entries(componentData.maintenanceActivities)) {
                        if (activity.nextDate && !isValidYear(new Date(activity.nextDate).getFullYear())) {
                            setSaveStatus('error');
                            console.error(`Invalid maintenance date for template ${templateId}: ${activity.nextDate}`);
                            setTimeout(() => setSaveStatus('idle'), 3000);
                            return;
                        }
                        if (activity.intervalValue !== undefined && !isValidInterval(activity.intervalValue)) {
                            setSaveStatus('error');
                            console.error(`Invalid maintenance interval for template ${templateId}: ${activity.intervalValue}`);
                            setTimeout(() => setSaveStatus('idle'), 3000);
                            return;
                        }
                    }
                }
            }

            // Find the component to get its templates and calculate default values
            const component = building?.model?.structure?.components?.[componentKey];
            if (!component) {
                console.error(`Component ${componentKey} not found`);
                return;
            }

            const componentImpl = new BaseComponentImpl(component);
            const templates = componentImpl.specificType ? getRelevantTemplates(componentImpl.specificType) : [];
            const lifetime = componentImpl.specificType ? componentLifetimes[componentImpl.specificType] : null;

            // Get current component data or create new
            const currentComponentData = maintenanceDraft.components[componentKey] || {
                maintenanceActivities: {}
            };

            // Prepare replacement date (editable first, then suggested, then calculated)
            let replacementDate = currentComponentData.replacementDate;
            if (!replacementDate && suggestedReplacementDates[componentKey]) {
                replacementDate = suggestedReplacementDates[componentKey];
            }
            if (!replacementDate && componentImpl.condition?.value && lifetime?.expectedLifetimeYears) {
                const calculatedDate = calculateReplacementDate(componentImpl, lifetime);
                if (calculatedDate) {
                    replacementDate = calculatedDate.getFullYear();
                }
            }

            // Calculate suggested replacement cost (for reference)
            const replacementCostData = calculateSuggestedReplacementCost(componentImpl, lifetime);
            let replacementCost = currentComponentData.replacementCost;
            let suggestedReplacementCost = currentComponentData.suggestedReplacementCost;
            let replacementCostCalculation = currentComponentData.replacementCostCalculation;
            
            // Always update suggested values for reference
            if (replacementCostData.cost > 0) {
                suggestedReplacementCost = replacementCostData.cost;
                replacementCostCalculation = replacementCostData.calculation;
                // Only set replacementCost if user hasn't set a value
                if (replacementCost === undefined) {
                    replacementCost = replacementCostData.cost;
                }
            }

            // Prepare maintenance activities
            const maintenanceActivities: { [templateId: string]: MaintenanceActivity } = { ...currentComponentData.maintenanceActivities };

            // Add/update maintenance activities for each template
            if (Array.isArray(templates) && templates.length > 0 && lifetime) {
                templates.forEach(template => {
                    if (!template || !template.id) return;

                    // Get current activity or create new
                    const directionalInterval = getIntervalForDirection(template, component.cardinalDirection?.value || null);
                    const currentActivity = maintenanceActivities[template.id] || {
                        nextDate: formatDateToString(new Date()),
                        intervalType: directionalInterval.interval_type,
                        intervalValue: directionalInterval.interval_value,
                        lastUpdated: new Date().toISOString()
                    };

                    // Update next date if not set (calculate directly)
                    if (!currentActivity.nextDate) {
                        const nextMaintenanceDate = calculateNextMaintenanceDate(componentImpl, template, lifetime);
                        if (nextMaintenanceDate) {
                            currentActivity.nextDate = formatDateToString(nextMaintenanceDate);
                        }
                    }

                    // Update interval if not set (use directional interval default)
                    if (!currentActivity.intervalValue) {
                        currentActivity.intervalType = directionalInterval.interval_type;
                        currentActivity.intervalValue = directionalInterval.interval_value;
                    }

                    // Calculate suggested maintenance cost (for reference)
                    const maintenanceCostData = calculateSuggestedMaintenanceCost(componentImpl, template);
                    
                    // Always update suggested values for reference
                    if (maintenanceCostData.cost > 0) {
                        currentActivity.suggestedCost = maintenanceCostData.cost;
                        currentActivity.costCalculation = maintenanceCostData.calculation;
                        // Only set cost if user hasn't set a value
                        if (currentActivity.cost === undefined) {
                            currentActivity.cost = maintenanceCostData.cost;
                        }
                    }

                    // Clean up undefined values before saving
                    const cleanActivity: MaintenanceActivity = {
                        nextDate: currentActivity.nextDate || formatDateToString(new Date()),
                        intervalType: currentActivity.intervalType || MaintenanceIntervalType.years,
                        intervalValue: currentActivity.intervalValue || 1,
                        ...(currentActivity.cost !== undefined && { cost: currentActivity.cost }),
                        ...(currentActivity.suggestedCost !== undefined && { suggestedCost: currentActivity.suggestedCost }),
                        ...(currentActivity.costCalculation && { costCalculation: currentActivity.costCalculation }),
                        lastUpdated: currentActivity.lastUpdated
                    };

                    maintenanceActivities[template.id] = cleanActivity;
                });
            }

            // Create updated component data - only include defined values
            const updatedComponentData: ComponentMaintenanceData = {
                ...(replacementDate !== undefined && { replacementDate }),
                ...(replacementCost !== undefined && { replacementCost }),
                ...(suggestedReplacementCost !== undefined && { suggestedReplacementCost }),
                ...(replacementCostCalculation && { replacementCostCalculation }),
                maintenanceActivities
            };

            // Update local state
            const updatedDraft: MaintenanceDraft = {
                ...maintenanceDraft,
                components: {
                    ...maintenanceDraft.components,
                    [componentKey]: updatedComponentData
                },
                metadata: {
                    ...maintenanceDraft.metadata,
                    savedAt: new Date().toISOString(),
                    buildingKey: buildingKey!
                }
            };

            setMaintenanceDraft(updatedDraft);

            // Save to Firebase
            const draftRef = ref(db, `maintenance/drafts/${buildingKey!}`);
            await set(draftRef, updatedDraft);


            setSaveStatus('saved');
            console.log(`Saved component ${componentKey} to draft with new structure`);
            
            setTimeout(() => {
                setSaveStatus('idle');
            }, 2000);
        } catch (error) {
            console.error('Error saving component to draft:', error);
            setSaveStatus('error');
            
            setTimeout(() => {
                setSaveStatus('idle');
            }, 5000);
        }
    };


    // Load maintenance plan draft from Firebase
    const loadMaintenancePlanDraft = async (): Promise<void> => {
        try {
            const draftRef = ref(db, `maintenance/drafts/${buildingKey!}`);
            const snapshot = await get(draftRef);
            
            if (snapshot.exists()) {
                const draftData = snapshot.val();
                console.log('Loaded maintenance plan draft:', draftData);
                
                // Check if this is the new structure or old structure
                if (draftData.components && draftData.metadata) {
                    // New structure - preserve all properties including rejectionComment
                    const loadedDraft: MaintenanceDraft = {
                        components: draftData.components || {},
                        metadata: {
                            savedAt: draftData.metadata.savedAt || new Date().toISOString(),
                            buildingKey: draftData.metadata.buildingKey || buildingKey!
                        },
                        // Preserve rejection comment if it exists
                        ...(draftData.rejectionComment && { rejectionComment: draftData.rejectionComment })
                    };
                    console.log('Loaded draft with rejection comment:', loadedDraft.rejectionComment);
                    setMaintenanceDraft(loadedDraft);
                    
                } else {
                    // Old structure - convert to new structure
                    console.log('Converting old draft structure to new structure');
                    const convertedDraft: MaintenanceDraft = {
                        components: {},
                        metadata: {
                            savedAt: draftData.savedAt || new Date().toISOString(),
                            buildingKey: draftData.buildingKey || buildingKey!
                        },
                        // Preserve rejection comment if it exists in old structure
                        ...(draftData.rejectionComment && { rejectionComment: draftData.rejectionComment })
                    };
                    
                    // Convert replacement dates
                    if (draftData.replacementDates) {
                        Object.entries(draftData.replacementDates).forEach(([componentKey, value]) => {
                            const year = typeof value === 'string' ? parseInt(value, 10) : (value as number);
                            if (!convertedDraft.components[componentKey]) {
                                convertedDraft.components[componentKey] = { maintenanceActivities: {} };
                            }
                            convertedDraft.components[componentKey].replacementDate = year;
                        });
                    }
                    
                    // Convert maintenance years and intervals
                    if (draftData.maintenanceYears || draftData.maintenanceIntervals) {
                        const allKeys = new Set([
                            ...Object.keys(draftData.maintenanceYears || {}),
                            ...Object.keys(draftData.maintenanceIntervals || {})
                        ]);
                        
                        allKeys.forEach(key => {
                            // Find the last underscore to split component key from template ID
                            const lastUnderscoreIndex = key.lastIndexOf('_');
                            if (lastUnderscoreIndex > 0) {
                                const componentKey = key.substring(0, lastUnderscoreIndex);
                                const templateId = key.substring(lastUnderscoreIndex + 1);
                                
                                if (!convertedDraft.components[componentKey]) {
                                    convertedDraft.components[componentKey] = { maintenanceActivities: {} };
                                }
                                
                                if (!convertedDraft.components[componentKey].maintenanceActivities[templateId]) {
                                    convertedDraft.components[componentKey].maintenanceActivities[templateId] = {
                                        nextDate: formatDateToString(new Date()),
                                        intervalType: MaintenanceIntervalType.years,
                                        intervalValue: 1,
                                        lastUpdated: new Date().toISOString()
                                    };
                                }
                                
                                // Set next date
                                if (draftData.maintenanceYears?.[key]) {
                                    const year = typeof draftData.maintenanceYears[key] === 'string' 
                                        ? parseInt(draftData.maintenanceYears[key], 10) 
                                        : draftData.maintenanceYears[key];
                                    convertedDraft.components[componentKey].maintenanceActivities[templateId].nextDate = formatDateToString(new Date(year, 0, 1));
                                }

                                // Set interval
                                if (draftData.maintenanceIntervals?.[key]) {
                                    const interval = typeof draftData.maintenanceIntervals[key] === 'string' 
                                        ? parseInt(draftData.maintenanceIntervals[key], 10) 
                                        : draftData.maintenanceIntervals[key];
                                    convertedDraft.components[componentKey].maintenanceActivities[templateId].intervalValue = interval;
                                }
                            }
                        });
                    }
                    
                    setMaintenanceDraft(convertedDraft);
                    
                }
            } else {
                console.log('No existing maintenance plan draft found');
            }
        } catch (error) {
            console.error('Error loading maintenance plan draft:', error);
        }
    };


    // Calculate projected install date based on condition and lifetime
    const calculateInstallDate = (component: BaseComponentImpl, lifetime: ComponentLifetime): Date | null => {
        if (!component.condition?.value || !component.condition.updatedWhen || !lifetime.expectedLifetimeYears) {
            return null;
        }

        const condition = component.condition.value;
        const assessmentDate = new Date(component.condition.updatedWhen);
        
        // Use directional lifetime if available
        const directionalLifetime = getLifetimeForDirection(lifetime, component.cardinalDirection?.value || null);
        const expectedLifetime = directionalLifetime.expectedLifetimeYears;

        // Calculate how much life has been used based on condition (10 = new, 0 = end of life)
        const lifeUsedPercent = (10 - condition) / 10;
        const yearsUsed = lifeUsedPercent * expectedLifetime;

        // Calculate install date by going back from assessment date
        const installDate = new Date(assessmentDate);
        installDate.setFullYear(installDate.getFullYear() - yearsUsed);

        return installDate;
    };

    // Generate timeline activities for a component
    const generateTimelineActivities = (
        component: BaseComponentImpl, 
        lifetime: ComponentLifetime, 
        templates: MaintenanceTemplate[]
    ) => {
        const installDate = calculateInstallDate(component, lifetime);
        if (!installDate) return [];

        const activities: Array<{
            year: number;
            type: 'replacement' | 'maintenance' | 'assessment';
            name: string;
            templateId?: string;
            isScheduled: boolean;
            color?: string;
            count?: number; // Number of occurrences in this year
        }> = [];

        const componentKey = component.key;
        const currentYear = new Date().getFullYear();
        
        // Use directional lifetime if available
        const directionalLifetime = getLifetimeForDirection(lifetime, component.cardinalDirection?.value || null);
        const expectedLifetime = directionalLifetime.expectedLifetimeYears;

        // Calculate replacement year
        const replacementYear = installDate.getFullYear() + expectedLifetime;
        
        // Get user-set replacement date or use calculated
        const userReplacementDate = maintenanceDraft.components[componentKey]?.replacementDate;
        const finalReplacementYear = userReplacementDate || replacementYear;

        activities.push({
            year: finalReplacementYear,
            type: 'replacement',
            name: 'Livslängd',
            isScheduled: !!userReplacementDate,
            templateId: `${componentKey}-replacement`,
            count: 1
        });

        // Add maintenance activities
        templates.forEach((template, templateIndex) => {
            if (!template.id) return;

            // Get user settings or defaults
            const userActivity = maintenanceDraft.components[componentKey]?.maintenanceActivities?.[template.id];
            
            // Skip disabled activities
            if (userActivity?.disabled === true) {
                return;
            }
            
            const nextDate = userActivity?.nextDate ? new Date(userActivity.nextDate) : calculateNextMaintenanceDate(component, template, lifetime) || new Date();
            const intervalValue = userActivity?.intervalValue || getIntervalForDirection(template, component.cardinalDirection?.value || null).interval_value;
            const intervalType = userActivity?.intervalType || getIntervalForDirection(template, component.cardinalDirection?.value || null).interval_type;

            // Assign color based on template index
            const color = getChartColor(templateIndex);

            // Calculate interval in days
            const intervalDays = intervalToDays(intervalType, intervalValue);
            const daysPerYear = 365.25;
            
            // Generate all maintenance activities from next due date until replacement
            let currentDate = new Date(nextDate);
            const replacementDate = new Date(finalReplacementYear, 11, 31); // End of replacement year
            
            // Track occurrences per year
            const occurrencesByYear = new Map<number, number>();
            
            while (currentDate < replacementDate) {
                const year = currentDate.getFullYear();
                if (year < finalReplacementYear) {
                    occurrencesByYear.set(year, (occurrencesByYear.get(year) || 0) + 1);
                }
                
                // Add interval
                currentDate = new Date(currentDate.getTime() + intervalDays * 24 * 60 * 60 * 1000);
            }
            
            // Create activities for each year with count
            occurrencesByYear.forEach((count, year) => {
                activities.push({
                    year: year,
                    type: 'maintenance',
                    name: template.name || 'Underhåll',
                    templateId: `${componentKey}-${template.id}`,
                    isScheduled: !!userActivity,
                    color: color,
                    count: count
                });
            });
        });

        // Add assessment activities for grouped components
        const componentBaseType = component.type;
        let assessmentCategory: ConditionAssessmentCategory | null = null;
        
        // Find which assessment group this component belongs to
        for (const group of BASE_TYPE_GROUPS) {
            if (group.baseTypes.includes(componentBaseType)) {
                assessmentCategory = getAssessmentForGroup(group.key);
                break;
            }
        }
        
        // Generate assessment activities if this component has assessments
        if (assessmentCategory) {
            const { value, unit } = assessmentCategory.interval;
            
            // Calculate interval in days
            let intervalDays = 0;
            switch (unit) {
                case 'days':
                    intervalDays = value;
                    break;
                case 'weeks':
                    intervalDays = value * 7;
                    break;
                case 'months':
                    intervalDays = value * 30.44; // Average days per month
                    break;
                case 'years':
                    intervalDays = value * 365.25;
                    break;
            }
            
            // Start from next scheduled assessment (current date + interval)
            const now = new Date();
            let assessmentDate = new Date(now.getTime() + intervalDays * 24 * 60 * 60 * 1000);
            const replacementDate = new Date(finalReplacementYear, 11, 31);
            
            // Track occurrences per year
            const assessmentsByYear = new Map<number, number>();
            
            while (assessmentDate < replacementDate) {
                const year = assessmentDate.getFullYear();
                if (year >= currentYear && year < finalReplacementYear) {
                    assessmentsByYear.set(year, (assessmentsByYear.get(year) || 0) + 1);
                }
                
                // Add interval
                assessmentDate = new Date(assessmentDate.getTime() + intervalDays * 24 * 60 * 60 * 1000);
            }
            
            // Create assessment activities for each year
            assessmentsByYear.forEach((count, year) => {
                activities.push({
                    year: year,
                    type: 'assessment',
                    name: 'Skickbedömning',
                    templateId: `${componentKey}-assessment-${year}`,
                    isScheduled: true,
                    count: count
                });
            });
        }

        return activities.sort((a, b) => a.year - b.year);
    };

    const renderComponentCard = (plan: ComponentWithReplacement) => {
        const componentKey = plan.component.key;
        const templates = plan.component.specificType ? getRelevantTemplates(plan.component.specificType) : [];
        const lifetime = plan.component.specificType ? componentLifetimes[plan.component.specificType] : null;
        
        // Create rows for this component
        const rows = [];
        
        // 1. Component header row (shows once per component)
        rows.push(
            <div key={`${componentKey}-header`} className="component-header-row">
                <div className="component-metadata">
                    <h6 className="component-name">
                        <a 
                            href={`/building/${buildingKey!}/component/${plan.component.key}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="component-link"
                        >
                        {building && plan.component.displayName(building)}
                        </a>
                    </h6>
                    <small className="component-type">
                        {plan.component.specificType && translate(plan.component.specificType)}
                    </small>
                </div>
                
                <div className="condition-display">
                    {plan.hasCondition && plan.component.condition?.value !== undefined && plan.component.condition.value !== null ? (
                        <div>
                            <div className="condition-badge" style={{ backgroundColor: getConditionColor(plan.component.condition.value) }}>
                                {plan.component.condition.value.toFixed(1)}/10
                            </div>
                            {plan.component.condition?.updatedWhen && (
                                <div className="assessment-date">
                                    <small className="text-muted">
                                        Skick bedömt: {formatDate(new Date(plan.component.condition.updatedWhen))}
                                    </small>
                                </div>
                            )}
                            {!plan.hasLifetime && (
                                <div className="missing-info">
                                    <small className="text-danger">
                                        Saknas livslängd
                                    </small>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="missing-info">
                            <small className="text-danger">
                                {!plan.hasCondition && !plan.hasLifetime ? 'Saknar skick & livslängd' :
                                 !plan.hasCondition ? 'Saknar skick' :
                                 !plan.hasLifetime ? 'Saknar livslängd' : '-'}
                            </small>
                        </div>
                    )}
                </div>
            </div>
        );

        // 2. Component images row (separate row below metadata)
        if (plan.component.photos && Object.keys(plan.component.photos).length > 0) {
            rows.push(
                <div key={`${componentKey}-images`} className="component-images-row">
                    <div className="images-container">
                        <PhotoGallery 
                            photos={plan.component.photos} 
                            buildingKey={buildingKey!} 
                        />
                    </div>
                </div>
            );
        }

        // Add activity headers if there are any activities to show
        const hasReplacement = plan.hasCondition && plan.hasLifetime && plan.replacementDate && plan.yearsRemaining !== null;
        const hasMaintenance = Array.isArray(templates) && templates.length > 0 && lifetime;
        
        if (hasReplacement || hasMaintenance) {
            rows.push(
                <div key={`${componentKey}-headers`} className="activity-headers">
                    <div className="activity-header-cell">Aktivitet</div>
                    <div className="activity-header-cell">Intervall</div>
                    <div className="activity-header-cell">Anpassat intervall</div>
                    <div className="activity-header-cell">Nästa gång</div>
                    <div className="activity-header-cell">Kostnad</div>
                    <div className="activity-header-cell">Status</div>
                            </div>
            );
        }

        // 3. Replacement row (if applicable)
        if (plan.hasCondition && plan.hasLifetime && plan.replacementDate && plan.yearsRemaining !== null) {
            rows.push(
                <div 
                    key={`${componentKey}-replacement`} 
                    className="activity-row replacement-row"
                    onMouseEnter={() => setHighlightedActivity(`${componentKey}-replacement`)}
                    onMouseLeave={() => setHighlightedActivity(null)}
                >
                    <div className="activity-name">
                        <span>Ersätt komponent</span>
                    </div>

                    <div className="activity-interval">
                        <span className="text-muted">{lifetime ? getDisplayLifetime(componentKey, lifetime) : 'N/A'} år</span>
                                </div>

                    <div className="activity-custom-interval">
                        <span className="text-muted">-</span>
                                </div>

                    <div className="activity-year">
                        <input
                            type="number"
                            className={`form-control form-control-sm ${isReplacementDateInvalid(componentKey) ? 'is-invalid' : ''}`}
                            value={getDisplayReplacementDate(plan)}
                            onChange={(e) => handleReplacementDateChange(plan.component.key, e.target.value)}
                            onBlur={() => handleReplacementDateBlur(plan.component.key)}
                            min="2024"
                            max="2100"
                            placeholder="År"
                        />
                        </div>

                    <div className="activity-cost">
                        <div className="cost-input-container">
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={getDisplayReplacementCost(componentKey)}
                                onChange={(e) => handleReplacementCostChange(componentKey, e.target.value)}
                                onBlur={() => handleReplacementCostBlur(componentKey)}
                                placeholder="0"
                                step="1"
                            />
                            <div className="cost-tooltip-container">
                                <Info 
                                    size={16}
                                    className="cost-tooltip-icon"
                                    onMouseEnter={() => setActiveTooltip(`replacement-${componentKey}`)}
                                    onMouseLeave={() => setActiveTooltip(null)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                {activeTooltip === `replacement-${componentKey}` && (
                                    <div 
                                        className="cost-tooltip-content"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {getReplacementCostCalculation(componentKey) || getReplacementCostExplanation(componentKey)}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="activity-years-left">
                        <span className="text-muted">{formatYears(plan.yearsRemaining)} kvar</span>
                    </div>
                </div>
            );
        }

        // 4. Maintenance activity rows
        if (Array.isArray(templates) && templates.length > 0 && lifetime) {
            templates.forEach(template => {
                if (!template || !template.id) return;
                
                const nextMaintenanceDate = calculateNextMaintenanceDate(plan.component, template, lifetime);
                const isOverdue = nextMaintenanceDate && nextMaintenanceDate < new Date();
                
                const templateIndex = templates.findIndex(t => t.id === template.id);
                const activityColor = getChartColor(templateIndex);
                
                const isDisabled = isMaintenanceActivityDisabled(componentKey, template.id);
                
                rows.push(
                    <div 
                        key={`${componentKey}-${template.id}`} 
                        className={`activity-row maintenance-row ${isOverdue ? 'overdue' : ''} ${isDisabled ? 'disabled' : ''}`}
                        onMouseEnter={() => setHighlightedActivity(`${componentKey}-${template.id}`)}
                        onMouseLeave={() => setHighlightedActivity(null)}
                    >
                        <div className="activity-name">
                            <div className="d-flex align-items-center">
                                <input
                                    type="checkbox"
                                    className="form-check-input me-2"
                                    checked={!isDisabled}
                                    onChange={() => handleMaintenanceActivityToggle(componentKey, template.id)}
                                    title={isDisabled ? "Aktivera aktivitet" : "Inaktivera aktivitet"}
                                />
                                <span className={isDisabled ? 'text-muted' : ''}>{template.name || 'Namnlös mall'}</span>
                            </div>
                        </div>

                        <div className="activity-interval">
                            {(() => {
                                const baseInterval = getDisplayBaseInterval(componentKey, template);
                                return <span className="text-muted">{baseInterval.value} {translateIntervalType(baseInterval.type)}</span>;
                            })()}
                        </div>

                        <div className="activity-custom-interval">
                            <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                <input
                                    type="number"
                                    className={`form-control form-control-sm ${isMaintenanceIntervalInvalid(componentKey, template.id) ? 'is-invalid' : ''}`}
                                    style={{ flex: '1', minWidth: '60px' }}
                                    value={getDisplayMaintenanceInterval(componentKey, template.id, template)}
                                    onChange={(e) => handleMaintenanceIntervalChange(componentKey, template.id, e.target.value)}
                                    onBlur={() => handleMaintenanceIntervalBlur(componentKey, template.id)}
                                    min="1"
                                    max="1000"
                                    placeholder="Värde"
                                    disabled={isDisabled}
                                />
                                <select
                                    className="form-select form-select-sm"
                                    style={{ flex: '1', minWidth: '90px' }}
                                    value={(() => {
                                        const activity = maintenanceDraft.components[componentKey]?.maintenanceActivities?.[template.id];
                                        if (activity?.intervalType) {
                                            return activity.intervalType;
                                        }
                                        const baseInterval = getDisplayBaseInterval(componentKey, template);
                                        return baseInterval.type;
                                    })()}
                                    onChange={(e) => handleMaintenanceIntervalTypeChange(componentKey, template.id, e.target.value as MaintenanceIntervalType)}
                                    disabled={isDisabled}
                                >
                                    <option value={MaintenanceIntervalType.days}>dagar</option>
                                    <option value={MaintenanceIntervalType.weeks}>veckor</option>
                                    <option value={MaintenanceIntervalType.months}>månader</option>
                                    <option value={MaintenanceIntervalType.years}>år</option>
                                </select>
                            </div>
                        </div>

                        <div className="activity-year">
                            <input
                                type="date"
                                className={`form-control form-control-sm ${isMaintenanceDateInvalid(componentKey, template.id) ? 'is-invalid' : ''}`}
                                value={getDisplayMaintenanceDate(componentKey, template.id, nextMaintenanceDate)}
                                onChange={(e) => handleMaintenanceDateChange(componentKey, template.id, e.target.value)}
                                onBlur={() => handleMaintenanceDateBlur(componentKey, template.id)}
                                placeholder="YYYY-MM-DD"
                                disabled={isDisabled}
                            />
                        </div>

                        <div className="activity-cost">
                            <div className="cost-input-container">
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    value={getDisplayMaintenanceCost(componentKey, template.id)}
                                    onChange={(e) => handleMaintenanceCostChange(componentKey, template.id, e.target.value)}
                                    onBlur={() => handleMaintenanceCostBlur(componentKey, template.id)}
                                    placeholder="0"
                                    step="1"
                                    disabled={isDisabled}
                                />
                                <div className="cost-tooltip-container">
                                    <Info 
                                        size={16}
                                        className="cost-tooltip-icon"
                                        onMouseEnter={() => setActiveTooltip(`maintenance-${componentKey}-${template.id}`)}
                                        onMouseLeave={() => setActiveTooltip(null)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    {activeTooltip === `maintenance-${componentKey}-${template.id}` && (
                                        <div 
                                            className="cost-tooltip-content"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {getMaintenanceCostCalculation(componentKey, template.id) || getMaintenanceCostExplanation(componentKey, template.id)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="activity-years-left">
                            {isOverdue ? (
                                <span className="text-danger">Försenad</span>
                            ) : (
                                <span className="text-muted">-</span>
                            )}
                        </div>
                    </div>
    );
            });
        }

        // Add timeline row if we have lifetime data
        if (lifetime && (plan.hasCondition && plan.hasLifetime)) {
            const installDate = calculateInstallDate(plan.component, lifetime);
            const timelineActivities = generateTimelineActivities(plan.component, lifetime, templates);
            
            if (installDate && timelineActivities.length > 0) {
                const currentYear = new Date().getFullYear();
                const startYear = currentYear;
                const endYear = Math.max(...timelineActivities.map(a => a.year));
                const totalYears = endYear - startYear;
                
                rows.push(
                    <div key={`${componentKey}-timeline`} className="component-timeline-row">
                        <div className="timeline-container">
                            <div className="timeline-header">
                                <h6 className="timeline-title">Komponentens aktiviteter över dess livslängd</h6>
                            </div>
                            
                            <div className="timeline-track">
                                <div className="timeline-line"></div>
                                
                                {/* Assessment markers above timeline */}
                                {(() => {
                                    // Filter only assessment activities
                                    const assessmentActivities = timelineActivities.filter(a => a.type === 'assessment');
                                    if (assessmentActivities.length === 0) return null;
                                    
                                    // Group by year
                                    const assessmentsByYear = assessmentActivities.reduce((acc, activity) => {
                                        if (!acc[activity.year]) {
                                            acc[activity.year] = [];
                                        }
                                        acc[activity.year].push(activity);
                                        return acc;
                                    }, {} as Record<number, typeof assessmentActivities>);

                                    return Object.entries(assessmentsByYear).map(([year, activities]) => {
                                        const yearNum = parseInt(year);
                                        const position = totalYears > 0 ? ((yearNum - startYear) / totalYears) * 100 : 0;
                                        
                                        return (
                                            <div
                                                key={`assessment-${year}`}
                                                className="timeline-year-activities"
                                                style={{ 
                                                    position: 'absolute',
                                                    top: '-10px',
                                                    left: `${position}%`,
                                                    transform: 'translateX(-50%)',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                {activities.map((activity, idx) => {
                                                    const isOverdue = activity.year < currentYear;
                                                    const showCount = activity.count && activity.count > 1;
                                                    
                                                    return (
                                                        <div
                                                            key={`assessment-marker-${year}-${idx}`}
                                                            className={`timeline-activity-marker assessment ${isOverdue ? 'overdue' : ''}`}
                                                            style={{
                                                                width: '24px',
                                                                height: '24px',
                                                                borderRadius: '50%',
                                                                backgroundColor: '#0d6efd',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: 'white',
                                                                cursor: 'pointer',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                                border: '2px solid white'
                                                            }}
                                                            title={`${activity.name} (${activity.year})${showCount ? ` - ${activity.count} gånger` : ''}`}
                                                        >
                                                            {showCount ? (
                                                                <span style={{ fontSize: '11px', fontWeight: 'bold' }}>{activity.count}</span>
                                                            ) : (
                                                                <Search size={14} />
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    });
                                })()}
                                
                                {/* Year markers */}
                                {(() => {
                                    // Calculate year label interval based on timeline length
                                    const getYearLabelInterval = (totalYears: number) => {
                                        if (totalYears <= 10) return 1;           // Show all labels for 10 years or less
                                        if (totalYears <= 20) return 2;          // Show every 2nd year for 11-20 years
                                        if (totalYears <= 40) return 5;          // Show every 5th year for 21-40 years
                                        return 10;                               // Show every 10th year for 40+ years
                                    };
                                    
                                    const labelInterval = getYearLabelInterval(totalYears);
                                    
                                    return Array.from({length: totalYears + 1}, (_, i) => {
                                        const year = startYear + i;
                                        const isCurrentYear = year === currentYear;
                                        const position = totalYears > 0 ? (i / totalYears) * 100 : 0;
                                        const isLastYear = i === totalYears;
                                        const shouldShowLabel = i % labelInterval === 0 || isCurrentYear || isLastYear;
                                        
                                        return (
                                            <div 
                                                key={`year-${year}`}
                                                className={`timeline-year-marker ${isCurrentYear ? 'current-year' : ''}`}
                                                style={{ left: `${position}%` }}
                                            >
                                                <div 
                                                    className="year-dot"
                                                    title={!shouldShowLabel ? year.toString() : undefined}
                                                ></div>
                                                {shouldShowLabel && <span className="year-label">{year}</span>}
                                            </div>
                                        );
                                    });
                                })()}
                                
                                {/* Activity markers (maintenance and replacement only) */}
                                {(() => {
                                    // Filter out assessment activities - they're shown above
                                    const nonAssessmentActivities = timelineActivities.filter(a => a.type !== 'assessment');
                                    
                                    // Group activities by year to handle multiple activities in same year
                                    const activitiesByYear = nonAssessmentActivities.reduce((acc, activity) => {
                                        if (!acc[activity.year]) {
                                            acc[activity.year] = [];
                                        }
                                        acc[activity.year].push(activity);
                                        return acc;
                                    }, {} as Record<number, typeof nonAssessmentActivities>);

                                    return Object.entries(activitiesByYear).map(([year, activities]) => {
                                        const yearNum = parseInt(year);
                                        const position = totalYears > 0 ? ((yearNum - startYear) / totalYears) * 100 : 0;
                                        
                                        return (
                                            <div
                                                key={`year-activities-${year}`}
                                                className="timeline-year-activities"
                                                style={{ left: `${position}%` }}
                                            >
                                                {activities.map((activity, activityIndex) => {
                                                    const isOverdue = activity.year < currentYear;
                                                    const isHighlighted = highlightedActivity === activity.templateId;
                                                    
                                                    return (
                                                        <div
                                                            key={`activity-${activity.year}-${activityIndex}`}
                                                            className={`timeline-activity-marker ${activity.type} ${isOverdue ? 'overdue' : ''} ${activity.isScheduled ? 'scheduled' : 'calculated'} ${isHighlighted ? 'highlighted' : ''}`}
                                                            title={`${activity.name} (${activity.year})`}
                                                        >
                                                            <div 
                                                                className="activity-dot"
                                                                style={activity.color ? { backgroundColor: activity.color } : {}}
                                                            >
                                                                {activity.type === 'replacement' ? (
                                                                    <RotateCcw size={12} />
                                                                ) : activity.count && activity.count > 1 ? (
                                                                    <span style={{ fontSize: '11px', fontWeight: 'bold' }}>{activity.count}</span>
                                                                ) : (
                                                                    <Wrench size={12} />
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </div>
                    </div>
                );
            }
        }

        // Add done button row at the bottom (only if component has complete information)
        const isDone = maintenanceDraft.components[componentKey]?.isDone || false;
        const hasIncompleteInfo = !plan.hasCondition || !plan.hasLifetime;
        
        if (!hasIncompleteInfo) {
            rows.push(
                <div key={`${componentKey}-done`} className="component-done-row">
                    <div className="done-button-container">
                        <button 
                            className={`btn btn-sm done-component-btn ${isDone ? 'done' : ''}`}
                            onClick={() => handleDoneToggle(componentKey)}
                        >
                            <Check size={16} className="me-1" />
                            {isDone ? 'Klar (klicka för att ta bort)' : 'Markera som klar'}
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div key={`component-container-${componentKey}`} className={`component-container ${isDone ? 'done' : ''}`}>
                {rows}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                    <h5 className="text-muted">Laddar underhållsplan...</h5>
                </div>
            </div>
        );
    }

    if (!building) {
        return (
            <div className="alert alert-warning">
                <h4>Byggnad hittades inte</h4>
                <p>Kunde inte ladda byggnadsinformation för nyckel: {buildingKey!}</p>
            </div>
        );
    }

    // Show proposed plan info if there's a pending proposal
    if (proposedPlan && proposedPlan.submission.status === 'pending') {
        const submitterName = userNames[proposedPlan.submission.submittedBy] || 'Okänd användare';
        const submissionDate = new Date(proposedPlan.submission.submittedAt).toLocaleDateString('sv-SE');
        const buildingName = building.summary?.name || 'Namnlös byggnad';

        return (
            <div className="container-fluid">
                <Helmet>
                    <title>{`Skapa underhållsplan - ${buildingName} - Molnfastigheter`}</title>
                </Helmet>
                <div className="alert alert-info">
                    <div className="d-flex align-items-center">
                        <AlertCircle size={24} className="me-3" />
                        <div className="flex-grow-1">
                            <h5 className="mb-1">Förslag väntar på granskning</h5>
                            <p className="mb-0">
                                {submitterName} lade in förslag på underhållsplan för {buildingName} den {submissionDate}.
                            </p>
                        </div>
                        <button 
                            className="btn btn-outline-primary"
                            onClick={handleWithdrawProposal}
                            disabled={withdrawing}
                        >
                            {withdrawing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Återkallar...
                                </>
                            ) : (
                                <>
                                    <Undo2 size={16} className="me-2" />
                                    Återkalla förslag
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const totalComponents = Object.values(groupedComponents).reduce((total, components) => total + components.length, 0);
    const totalIncompleteComponents = Object.values(groupedIncompleteComponents).reduce((total, components) => total + components.length, 0);
    const componentsNeedingReplacement = Object.values(groupedComponents).reduce((total, components) => 
        total + components.filter(plan => plan.yearsRemaining !== null && plan.yearsRemaining <= 5).length, 0
    );

    return (
        <div className="maintenance-draft">
            <Helmet>
                <title>{`Skapa underhållsplan - ${building.summary?.name || 'Byggnad'} - Molnfastigheter`}</title>
            </Helmet>
            {/* Top Navigation Buttons */}
            <div className="top-navigation">
                <div className="back-button-container">
                    <button 
                        className="btn btn-outline-secondary"
                        onClick={() => navigate(`/building/${buildingKey}`)}
                    >
                        <ArrowLeft size={20} className="me-2" />
                        Tillbaka till byggnad
                    </button>
                </div>
                <div className="action-buttons-container">
                    <button 
                        className="btn btn-outline-danger"
                        onClick={handleDeleteDraft}
                    >
                        <Trash2 size={20} className="me-2" />
                        Radera utkast
                    </button>
                    <button 
                        className="btn btn-primary"
                        onClick={() => {
                            // Additional validation before opening preview
                            if (!validateDataLoaded()) {
                                alert('Vänta tills all data har laddats klart innan du förhandsgranskar underhållsplanen.');
                                return;
                            }
                            
                            // Check if there are any maintenance components
                            const hasMaintenanceData = Object.keys(maintenanceDraft.components || {}).length > 0;
                            if (!hasMaintenanceData) {
                                alert('Underhållsplanen innehåller inga komponenter med underhållsaktiviteter. Lägg till underhållsaktiviteter innan förhandsvisning.');
                                return;
                            }
                            
                            setShowPreviewModal(true);
                        }}
                        disabled={!dataFullyLoaded}
                        title={!dataFullyLoaded ? 'Väntar på att all data ska laddas...' : 'Förhandsgranska underhållsplan'}
                    >
                        <Eye size={20} className="me-2" />
                        Förhandsgranska
                        {!dataFullyLoaded && <span className="ms-2 spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
                    </button>
                </div>
            </div>

            {/* Header */}
            <div className="header-section">
                <div className="header-content" style={{marginTop: '30px', marginBottom: '30px'}} >
                    <h1 className="display-6 fw-bold text-dark">
                        Underhållsplan: {building.summary?.name || 'Namnlös byggnad'}
                    </h1>
                    <p className="lead text-muted">
                        Underhållsplan baserat på komponenternas skick och förväntad livslängd
                    </p>
                </div>
            </div>

            {/* Rejection Comment */}
            {maintenanceDraft.rejectionComment && (
                <div className="rejection-comment-section">
                    <div className="row justify-content-center">
                        <div className="col-md-10">
                            <div className="alert alert-danger">
                                <div className="d-flex align-items-start">
                                    <AlertCircle size={24} className="me-3 mt-1 text-danger flex-shrink-0" />
                                    <div className="flex-grow-1">
                                        <h5 className="alert-heading mb-2">
                                            Plan avvisad för granskning
                                        </h5>
                                        <div className="mb-2">
                                            <strong>Anledning:</strong>
                                        </div>
                                        <p className="mb-3" style={{ whiteSpace: 'pre-wrap' }}>
                                            {maintenanceDraft.rejectionComment.comment}
                                        </p>
                                        <div className="d-flex justify-content-between align-items-center text-muted">
                                            <small>
                                                <strong>Avvisad av:</strong> {userNames[maintenanceDraft.rejectionComment.rejectedBy] || 'Okänd användare'}
                                            </small>
                                            <small>
                                                <strong>Datum:</strong> {new Date(maintenanceDraft.rejectionComment.rejectedAt).toLocaleString('sv-SE')}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <div className="main-content-section">
                <div className="container-constrained">
                    

            {/* Components by BaseType */}
            <div className="components-sections">
                {BASE_TYPE_GROUPS.map((group) => {
                    // Combine components from all BaseTypes in this group
                    const components = group.baseTypes.flatMap(baseType => groupedComponents[baseType] || []);
                    if (components.length === 0) return null;

                    const sectionKey = `basetype-${group.key}`;
                    const isExpanded = expandedSections.has(sectionKey);

                    return (
                        <div key={group.key} className="card mb-3">
                            <div
                                className="card-header cursor-pointer"
                                onClick={() => toggleSection(sectionKey)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="row align-items-center">
                                    <div className="col">
                                        <h5 className="mb-0">
                                            {isExpanded ? <ChevronDown size={20} className="me-2" /> : <ChevronRight size={20} className="me-2" />}
                                            {group.label} ({components.length})
                                        </h5>
                                    </div>
                                    <div className="col-auto">
                                        {(() => {
                                            const doneCount = countDoneComponents(components);
                                            const totalCount = components.length;
                                            const isAllDone = doneCount === totalCount;
                                            return (
                                                <span className={`badge ${isAllDone ? 'bg-success' : 'bg-secondary'}`}>
                                                    Underhållsplan för {doneCount}/{totalCount}
                                        </span>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="card-body p-0">
                                    {(() => {
                                        const assessment = getAssessmentForGroup(group.key);
                                        if (assessment) {
                                            const intervalUnit = assessment.interval.unit === 'days' ? 'dagar' :
                                                                assessment.interval.unit === 'weeks' ? 'veckor' :
                                                                assessment.interval.unit === 'months' ? 'månader' : 'år';
                                            const nextDate = calculateNextAssessmentDate(assessment);
                                            return (
                                                <div className="activity-row" style={{ 
                                                    backgroundColor: '#f8f9fa', 
                                                    borderBottom: '2px solid #dee2e6',
                                                    fontWeight: 500
                                                }}>
                                                    <div className="activity-name">
                                                        <div className="d-flex align-items-center">
                                                            <Search size={16} className="me-2 text-primary" />
                                                            <span>Skickbedömning</span>
                                                        </div>
                                                    </div>
                                                    <div className="activity-interval">
                                                        <span className="text-muted">
                                                            {assessment.interval.value} {intervalUnit}
                                                        </span>
                                                    </div>
                                                    <div className="activity-custom-interval">
                                                        <span className="text-muted">-</span>
                                                    </div>
                                                    <div className="activity-year">
                                                        <span className="text-muted">{nextDate}</span>
                                                    </div>
                                                    <div className="activity-years-left">
                                                        <span className="text-muted">-</span>
                                                    </div>
                                                    <div className="activity-cost">
                                                        {assessment.instruction && (
                                                            <div className="cost-tooltip-container">
                                                                <Info 
                                                                    size={16} 
                                                                    className="cost-tooltip-icon"
                                                                    onMouseEnter={() => setActiveTooltip(`assessment-${group.key}`)}
                                                                    onMouseLeave={() => setActiveTooltip(null)}
                                                                />
                                                                {activeTooltip === `assessment-${group.key}` && (
                                                                    <div className="cost-tooltip-content" style={{ whiteSpace: 'normal', maxWidth: '300px' }}>
                                                                        {assessment.instruction}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                    <div className="p-3">
                                        {components.map(plan => renderComponentCard(plan))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Incomplete Components by BaseType */}
                {BASE_TYPE_GROUPS.map((group) => {
                    // Combine incomplete components from all BaseTypes in this group
                    const incompleteComponents = group.baseTypes.flatMap(baseType => groupedIncompleteComponents[baseType] || []);
                    if (incompleteComponents.length === 0) return null;

                    const sectionKey = `incomplete-${group.key}`;
                    const isExpanded = expandedSections.has(sectionKey);

                    return (
                        <div key={`incomplete-${group.key}`} className="card mb-3 border-warning">
                        <div
                            className="card-header bg-light cursor-pointer"
                                onClick={() => toggleSection(sectionKey)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="row align-items-center">
                                <div className="col">
                                    <h5 className="mb-0 text-warning">
                                            {isExpanded ? <ChevronDown size={20} className="me-2" /> : <ChevronRight size={20} className="me-2" />}
                                            {group.label} - Ofullständig information ({incompleteComponents.length})
                                    </h5>
                                    <small className="text-muted">
                                        Komponenter som saknar skickbedömning eller livslängd
                                    </small>
                                </div>
                                <div className="col-auto">
                                    <span className="badge bg-warning text-dark">Kräver åtgärd</span>
                                </div>
                            </div>
                        </div>

                            {isExpanded && (
                            <div className="card-body p-0">
                                {(() => {
                                    const assessment = getAssessmentForGroup(group.key);
                                    if (assessment) {
                                        const intervalUnit = assessment.interval.unit === 'days' ? 'dagar' :
                                                            assessment.interval.unit === 'weeks' ? 'veckor' :
                                                            assessment.interval.unit === 'months' ? 'månader' : 'år';
                                        const nextDate = calculateNextAssessmentDate(assessment);
                                        return (
                                            <div className="activity-row" style={{ 
                                                backgroundColor: '#f8f9fa', 
                                                borderBottom: '2px solid #dee2e6',
                                                fontWeight: 500
                                            }}>
                                                <div className="activity-name">
                                                    <div className="d-flex align-items-center">
                                                        <Search size={16} className="me-2 text-primary" />
                                                        <span>Skickbedömning</span>
                                                    </div>
                                                </div>
                                                <div className="activity-interval">
                                                    <span className="text-muted">
                                                        {assessment.interval.value} {intervalUnit}
                                                    </span>
                                                </div>
                                                <div className="activity-custom-interval">
                                                    <span className="text-muted">-</span>
                                                </div>
                                                <div className="activity-year">
                                                    <span className="text-muted">{nextDate}</span>
                                                </div>
                                                <div className="activity-years-left">
                                                    <span className="text-muted">-</span>
                                                </div>
                                                <div className="activity-cost">
                                                    {assessment.instruction && (
                                                        <div className="cost-tooltip-container">
                                                            <Info 
                                                                size={16} 
                                                                className="cost-tooltip-icon"
                                                                onMouseEnter={() => setActiveTooltip(`assessment-incomplete-${group.key}`)}
                                                                onMouseLeave={() => setActiveTooltip(null)}
                                                            />
                                                            {activeTooltip === `assessment-incomplete-${group.key}` && (
                                                                <div className="cost-tooltip-content" style={{ whiteSpace: 'normal', maxWidth: '300px' }}>
                                                                    {assessment.instruction}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                                <div className="p-3">
                                        {incompleteComponents.map(plan => renderComponentCard(plan))}
                                </div>
                            </div>
                        )}
                    </div>
                    );
                })}

                {Object.values(groupedComponents).every(components => components.length === 0) && Object.values(groupedIncompleteComponents).every(components => components.length === 0) && (
                    <div className="alert alert-info">
                        <h4>Inga komponenter hittades</h4>
                        <p>Denna byggnad har inga komponenter registrerade för underhållsplanering.</p>
                    </div>
                )}
            </div>


            {/* Delete Draft Modal */}
            <div className={`modal fade ${showDeleteModal ? 'show' : ''}`} 
                 style={{ display: showDeleteModal ? 'block' : 'none' }} 
                 tabIndex={-1} 
                 role="dialog"
                 aria-labelledby="deleteDraftModalLabel"
                 aria-hidden={!showDeleteModal}>
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="deleteDraftModalLabel">
                                <Trash2 size={20} className="me-2" />
                                Radera utkast
                            </h5>
                            <button 
                                type="button" 
                                className="btn-close" 
                                onClick={handleCancelDeleteDraft}
                                aria-label="Stäng"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <div className="alert alert-warning">
                                <strong>Varning:</strong> Denna åtgärd kan inte ångras.
                            </div>
                            <p className="mb-0">
                                <strong>Är du säker?</strong> Alla data i utkastet kommer att raderas permanent.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={handleCancelDeleteDraft}
                                disabled={deleting}
                            >
                                Avbryt
                            </button>
                            <button 
                                type="button" 
                                className="btn btn-danger" 
                                onClick={handleConfirmDeleteDraft}
                                disabled={deleting}
                            >
                                {deleting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Raderar...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={16} className="me-2" />
                                        Radera utkast
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
                </div>
            </div>

            {/* Modal backdrop */}
            {showDeleteModal && (
                <div className="modal-backdrop fade show"></div>
            )}

            {/* Preview Modal */}
            {showPreviewModal && (
                <div 
                    className="modal show d-block" 
                    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowPreviewModal(false);
                        }
                    }}
                >
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 'fit-content', width: 'auto', minWidth: '1100px', margin: '20px auto'}}>
                        <div className="modal-content" >
                            <div className="modal-header">
                                <h5 className="modal-title" >Förhandsgranska underhållsplan</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowPreviewModal(false)}
                                    aria-label="Close"
                                >
                                </button>
                            </div>
                            <div className="modal-body p-0" >
                                <MaintenancePreview showBackButton={false} />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MaintenanceDraft;
